import Head from 'expo-router/head';
import { useState } from 'react';
import { ScrollView } from 'react-native';
import { getTokenValue, Text, XStack, YStack } from 'tamagui';

import PSCircleButton from '@/components/ui/PSCircleButton';
import PSIcon from '@/components/ui/PSIcon';

/**
 * ── /components/ps-circle-button ─────────────────────────────────────────
 *
 * PSCircleButton across its real surface: the `normal` variant idle vs
 * `filled` (the hairline-border-only look vs the solid-fill-for-contrast
 * look), `disabled`, the `borderColor`/icon-color override TicketTier's
 * stepper uses while a step stays pressable, and `bare` (no chrome at all —
 * for headers that already supply the surface). Every group below is lifted
 * from a real call site, cited in its own comment; nothing here is a made-up
 * combination PSCircleButton doesn't actually ship.
 * ─────────────────────────────────────────────────────────────────────────
 */

// Live stepper demo geometry — mirrors TicketTier's own bounds (0..numTickets)
// closely enough to exercise both disabled edges without pulling in the real
// component's ticket-tier fixtures.
const STEPPER_MAX = 3;

export default function PSCircleButtonPreview() {
  // Drives the live share-button demo below — copies "succeeding" for 2s,
  // same shape as events/[id]/index.tsx's onShare -> didCopyLink.
  const [didCopyLink, setDidCopyLink] = useState(false);
  // Drives the live TicketTier-stepper demo.
  const [quantity, setQuantity] = useState(0);

  return (
    <>
      <Head>
        <title>PSCircleButton · Putting Scene</title>
      </Head>
      <ScrollView
        style={{
          flex: 1,
          backgroundColor: getTokenValue('$color.background', 'color'),
        }}
      >
        <YStack items="flex-start" gap="$lg" p="$lg">
          {/* normal, unfilled — the DEFAULT (no `variant`, no `filled`): hairline
            `$borderColor` edge, transparent fill. For a control that sits in
            plain page content with nothing behind it to supply an edge — the
            modal-style "close" (✕) dismiss on cancel-membership and
            profile/edit. `disabled` is carried over from the old all.tsx
            holding-pen snippet (it isn't a real call site for THIS variant,
            but the dim-to-0.35/no-press behavior is generic to the atom, so
            it's shown once here rather than only on the filled stepper
            below). */}
          <YStack gap="$xs">
            <Text fontFamily="$body" fontSize="$small" color="$textDarkMuted">
              normal, unfilled (default) — idle vs disabled: my-memberships
              cancel&apos;s and profile/edit&apos;s &quot;close&quot; dismiss
            </Text>
            <XStack gap="$md" items="center">
              <PSCircleButton onPress={() => {}}>
                <PSIcon name="close" />
              </PSCircleButton>
              <PSCircleButton disabled>
                <PSIcon name="close" />
              </PSCircleButton>
            </XStack>
          </YStack>

          {/* normal, filled — solid `$background` fill so the glyph keeps
            contrast sitting over a photo/hero (BackAndShareHeader's back +
            share pair, events/[id]'s header, EventDetailSkeleton's back-only
            skeleton state, and PSHeaderBackButtonOnTransparentHeader on the
            bookings/confirm headers). The share button is LIVE — tap it to
            watch the glyph flip to a checkmark for 2s, the same
            copy-succeeded receipt events/[id]/index.tsx shows on platforms
            with no native share sheet. */}
          <YStack gap="$xs">
            <Text fontFamily="$body" fontSize="$small" color="$textDarkMuted">
              normal, filled — contrast fill for a hero/photo backdrop:
              BackAndShareHeader, events/[id] header, EventDetailSkeleton
            </Text>
            <XStack gap="$md" items="center">
              <PSCircleButton filled onPress={() => {}}>
                <PSIcon name="arrow-back" />
              </PSCircleButton>
              <PSCircleButton
                filled
                onPress={() => {
                  setDidCopyLink(true);
                  setTimeout(() => setDidCopyLink(false), 2000);
                }}
              >
                <PSIcon
                  name={didCopyLink ? 'checkmark' : 'share-outline'}
                  size={18}
                />
              </PSCircleButton>
            </XStack>
          </YStack>

          {/* filled + disabled + the `borderColor` override — TicketTier's
            quantity stepper, its one real call site: `filled` for contrast,
            `disabled` (with the default gray border/icon) at a bound, and a
            `borderColor="$color.brand"` + brand-colored icon on whichever
            step stays pressable — the prop the component's own docstring
            calls out as existing for exactly this caller. LIVE: tap +/- to
            watch minus disable at 0 and plus disable at STEPPER_MAX, with
            the brand border/icon following whichever end is still live. */}
          <YStack gap="$xs">
            <Text fontFamily="$body" fontSize="$small" color="$textDarkMuted">
              filled + disabled + brand `borderColor` override —
              TicketTier&apos;s quantity stepper (minus disables at 0, plus at
              max)
            </Text>
            <XStack gap="$md" items="center">
              <PSCircleButton
                filled
                disabled={quantity === 0}
                borderColor={quantity === 0 ? '$borderColor' : '$color.brand'}
                onPress={() => setQuantity((q) => Math.max(0, q - 1))}
              >
                <PSIcon
                  name="remove"
                  size={16}
                  color={quantity === 0 ? '$textDark' : '$brand'}
                />
              </PSCircleButton>
              <Text
                fontFamily="$body"
                fontSize="$base"
                color="$brand"
                text="center"
                width={16}
              >
                {quantity}
              </Text>
              <PSCircleButton
                filled
                disabled={quantity === STEPPER_MAX}
                onPress={() => setQuantity((q) => Math.min(STEPPER_MAX, q + 1))}
                borderColor={
                  quantity === STEPPER_MAX ? '$borderColor' : '$color.brand'
                }
              >
                <PSIcon
                  name="add"
                  size={16}
                  color={quantity === STEPPER_MAX ? '$textDark' : '$brand'}
                />
              </PSCircleButton>
            </XStack>
          </YStack>

          {/* bare — no border, no fill, just the glyph: for headers that
            already supply their own surface (the native header bar, the home
            hero). PSHeaderBackButton/PSHeaderSettingsButton
            (PSHeaderButtons.tsx) are the header-chrome defaults; memberships'
            back button and HomeHeaderRight's account icon show the variant
            taking a custom icon `color`/`size` (memberships tints the arrow
            `$borderColor` to sit quietly against its dark hero; the account
            icon goes `$brandSecondary` at 25 to read as a brand accent). The
            type forbids `filled`/`backgroundColor`/`borderColor` on `bare` —
            passing any would be silently ignored, so PSCircleButton's own
            prop type refuses them instead. */}
          <YStack gap="$xs">
            <Text fontFamily="$body" fontSize="$small" color="$textDarkMuted">
              bare — no chrome; the header itself is the surface:
              PSHeaderBackButton, PSHeaderSettingsButton, memberships&apos;
              back, HomeHeaderRight&apos;s account icon
            </Text>
            <XStack gap="$md" items="center">
              <PSCircleButton variant="bare" onPress={() => {}}>
                <PSIcon name="arrow-back" />
              </PSCircleButton>
              <PSCircleButton variant="bare" onPress={() => {}}>
                <PSIcon name="settings-outline" />
              </PSCircleButton>
              <PSCircleButton variant="bare" onPress={() => {}}>
                <PSIcon name="arrow-back" color="$borderColor" />
              </PSCircleButton>
              <PSCircleButton variant="bare" onPress={() => {}}>
                <PSIcon
                  name="person-outline"
                  color="$brandSecondary"
                  size={25}
                />
              </PSCircleButton>
            </XStack>
          </YStack>

          {/* bare on a dark surface — PSToastHost's close control: the toast
            card itself is `$textDark`/`$error`, so its close glyph goes
            `$textWhite`. Shown here on a `$brandDark` stand-in (same trick
            ps-button.tsx uses for its "toolbar" variant) because a white
            glyph on this page's light background would just vanish. */}
          <YStack bg="$color.brandDark" p="$space.lg" gap="$sm" rounded="$base">
            <Text fontFamily="$body" fontSize="$small" color="$textWhiteMuted">
              bare, white icon — PSToastHost&apos;s close control, shown on a
              dark stand-in for the toast card it really sits on
            </Text>
            <PSCircleButton variant="bare" onPress={() => {}}>
              <PSIcon name="close-outline" color="$textWhite" size={18} />
            </PSCircleButton>
          </YStack>
        </YStack>
      </ScrollView>
    </>
  );
}
